import { useState } from "react"
import PropTypes from "prop-types"
import { Link, Router, navigate } from "@reach/router"
import { Table } from "components/ui"
import { Button } from "@planningcenter/tapestry-react"
import AreaForm from "./AreaForm"
import { api } from "utils/api"
import { genderToString, ageToString, gradeToString, attendeeTypeToString } from "./areaRestriction"
import sortBy from "utils/sorter"
import { isPresent, isBlank, pluralize } from "utils/helpers"

const renderCapacity = (row, _column) => {
  return `${row.capacity - row.capacityRemaining} / ${row.capacity}`
}

const ManageAreas = ({
  eventStartDate,
  onCreate,
  onDelete,
  areas,
  gradeOptions,
  genderOptions,
  onDragEnd,
  assignmentType,
  onAssignmentTypeSave,
  basePath,
  attendeeTypes,
  signupFeatures
}) => {
  const [assignmentTypeForm, setAssignmentTypeForm] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)
  const [validation, setValidation] = useState({})
  const [name, setName] = useState(assignmentType.name || "")

  const renderDeleteButton = (row, _column) => {
    return (
      row.canDelete && (
        <Button
          icon={{ name: "general.trash" }}
          tooltip={{ title: "Delete" }}
          variant="naked"
          theme="destroy"
          onClick={e => handleDelete(row, e)}
        />
      )
    )
  }

  const renderHeaderText = () => {
    return !assignmentTypeForm ? (
      assignmentType.name
    ) : (
      <input
        className={`${validation.name && "error-input"}`}
        type="text"
        name="name"
        placeholder="Name"
        value={name}
        onChange={e => setName(e.target.value)}
        css={{
          marginTop: "-6px !important",
          height: "30px !important",
          maxWidth: 300
        }}
      />
    )
  }

  const renderHeaderActions = () => {
    return !assignmentTypeForm
      ? [
          {
            onClick: () => setAssignmentTypeForm(true),
            size: "sm",
            icon: { name: "general.pencil" },
            tooltip: { title: "Edit" },
            variant: "naked"
          }
        ]
      : [
          {
            onClick: handleAssignmentTypeSave,
            size: "xs",
            icon: { name: "general.check" },
            tooltip: { title: "Save" },
            theme: "create",
            variant: "fill"
          },
          {
            onClick: handleAssignmentTypeCancel,
            size: "xs",
            icon: { name: "general.x" },
            tooltip: { title: "Cancel" },
            variant: "fill"
          }
        ]
  }

  const columns = [
    {
      cell: "name",
      label: "Name",
      style: { width: "35%" }
    },
    ...(signupFeatures.includes("named_attendees")
      ? [
          {
            cell: "genderToS",
            label: "Gender",
            style: { width: "15%" }
          },
          {
            cell: "ageGradeToS",
            label: "Age / Grade",
            style: { width: "15%" }
          }
        ]
      : []),
    {
      cell: "attendeeTypeToS",
      label: "Selection",
      style: { width: "15%" }
    },
    {
      cell: "capacity",
      label: "Capacity",
      style: { width: "15%" },
      render: renderCapacity
    },
    {
      cell: "actions",
      label: "",
      style: { textAlign: "right", width: "5%" },
      render: renderDeleteButton
    }
  ]

  const handleRowClick = row => navigate(`${basePath}/areas/${row.id}/edit`)

  const handleDelete = (row, e) => {
    setSaveStatus({
      text: "Saving...",
      icon: "spinner"
    })

    e.stopPropagation()

    return onDelete(row.paths.show)
      .then(r => {
        setSaveStatus({
          text: "Deleted!",
          icon: "general#check",
          type: "success"
        })

        if (isBlank(r.data)) {
          return navigate(`${basePath}`)
        }
      })
      .catch(_error => {
        setSaveStatus({
          text: "Deletion failed.",
          icon: "general#exclamation-triangle",
          type: "error"
        })
      })
      .finally(_r => setTimeout(() => setSaveStatus(null), 2000))
  }

  const handleCancel = () => navigate(`${basePath}/areas/manage`)

  const handleSave = params => {
    return onCreate(params).then(resp => {
      navigate(`${basePath}/areas/manage`)
      return resp
    })
  }

  const handleAssignmentTypeSave = () => {
    if (isPresent(validateForm())) {
      return
    }

    return api.put(assignmentType.paths.show, { name }).then(resp => {
      onAssignmentTypeSave(resp.data)
      setAssignmentTypeForm(false)
    })
  }

  const handleAssignmentTypeCancel = () => {
    setAssignmentTypeForm(false)
    setName(assignmentType.name)
  }

  const validateForm = () => {
    let validation = {}

    if (isBlank(name)) {
      validation.name = "Name can not be blank."
    }

    setValidation(validation)
    return validation
  }

  const rows = sortBy("position", Object.values(areas)).map(area => ({
    ...area,
    genderToS: genderToString(area.gender, "Any"),
    ageGradeToS:
      gradeToString(area.minGrade, area.maxGrade) ||
      ageToString(area.minAge, area.maxAge, area.ageRestrictionUnit, "Any"),
    attendeeTypeToS: attendeeTypeToString(
      area.hasAttendeeTypeRestriction,
      area.attendeeTypeRestrictions,
      "Any"
    ),
    className: "va-t"
  }))

  return (
    <Router primary={false}>
      <AreaForm
        path="new"
        eventStartDate={eventStartDate}
        onCancel={handleCancel}
        onSave={handleSave}
        gradeOptions={gradeOptions}
        genderOptions={genderOptions}
        area={{
          attendeeTypeRestrictions: attendeeTypes.map(at => ({
            ...at,
            isAvailable: false
          }))
        }}
        signupFeatures={signupFeatures}
      />

      {rows.length !== 0 && (
        <Table
          path="/"
          heading={{
            headerText: renderHeaderText(),
            headerSubText: pluralize("area", "areas", rows.length),
            headerActions: renderHeaderActions()
          }}
          headingChildren={
            <Button as={Link} theme="create" to={`${basePath}/areas/manage/new`} title="Add area" />
          }
          onRowClick={handleRowClick}
          rows={rows}
          draggableType="handle"
          onDragEnd={onDragEnd}
          columns={columns}
          status={saveStatus}
        />
      )}
    </Router>
  )
}

ManageAreas.propTypes = {
  basePath: PropTypes.string,
  eventStartDate: PropTypes.string,
  onDragEnd: PropTypes.func,
  onCreate: PropTypes.func,
  onDelete: PropTypes.func,
  onTypeEdit: PropTypes.func,
  areas: PropTypes.shape({}),
  attendeeTypes: PropTypes.array,
  gradeOptions: PropTypes.shape({}),
  genderOptions: PropTypes.shape({}),
  assignment: PropTypes.shape({}),
  assignmentType: PropTypes.shape({
    name: PropTypes.string,
    paths: PropTypes.shape({
      show: PropTypes.string
    })
  }),
  onAssignmentTypeSave: PropTypes.func,
  location: PropTypes.shape({
    pathname: PropTypes.string
  }),
  signupFeatures: PropTypes.array
}

export default ManageAreas
